import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MetrikReihe } from '../api';

/**
 * Ein Liniendiagramm über eine Messreihe.
 *
 * OHNE BIBLIOTHEK. Das Bündel der Oberfläche wird beim Bauen schon als zu groß
 * gemeldet; eine Diagrammbibliothek für vier Liniendiagramme wäre die falsche
 * Antwort darauf. Was hier fehlt — Zoom, mehrere Achsen, Legenden — brauchen
 * diese Ansichten nicht.
 *
 * WAS ES RICHTIG MACHEN MUSS: **Lücken zeigen statt sie zu überbrücken.** Eine
 * Linie, die über eine fehlende Stunde durchgezogen wird, behauptet einen
 * Verlauf, den niemand gemessen hat — und genau diese Stunde ist die, in der
 * die Maschine weg war. Deshalb wird der Pfad an jedem `null` unterbrochen.
 */

const HOEHE = 120;
const RAND = { oben: 8, unten: 18, links: 46, rechts: 6 };

function formatiere(wert: number, einheit: MetrikReihe['einheit']): string {
  switch (einheit) {
    case 'bytes': {
      const einheiten = ['B', 'KB', 'MB', 'GB', 'TB'];
      let w = wert;
      let i = 0;
      while (w >= 1024 && i < einheiten.length - 1) {
        w /= 1024;
        i++;
      }
      return `${w.toFixed(w < 10 && i > 0 ? 1 : 0)} ${einheiten[i]}`;
    }
    case 'prozent':
      return `${wert.toFixed(0)} %`;
    case 'sekunden':
      return wert < 1 ? `${(wert * 1000).toFixed(0)} ms` : `${wert.toFixed(1)} s`;
    case 'proSekunde':
      return wert < 10 ? wert.toFixed(2) : wert.toFixed(0);
    default:
      return wert < 10 && !Number.isInteger(wert) ? wert.toFixed(1) : wert.toFixed(0);
  }
}

function zeitLabel(t: number, spanneSekunden: number): string {
  const d = new Date(t * 1000);
  // Über eine Woche ist die Uhrzeit unwichtig, darunter das Datum.
  return spanneSekunden > 604800
    ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
    : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export default function Verlaufsgraph({
  reihe,
  breite = 640,
  /** Wird als gestrichelte Linie gezeichnet — etwa eine Speichergrenze. */
  grenze,
  grenzeLabel,
}: {
  reihe: MetrikReihe;
  breite?: number;
  grenze?: number | null;
  grenzeLabel?: string;
}) {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const [zeiger, setZeiger] = useState<{ x: number; i: number } | null>(null);

  const { pfade, minY, maxY, punkte, spanne } = useMemo(() => {
    const p = reihe.punkte;
    const werte = p.map((x) => x.v).filter((v): v is number => v !== null);

    // Ohne Werte gibt es nichts zu skalieren. 0..1 verhindert eine Division
    // durch null weiter unten.
    const roh = { min: werte.length ? Math.min(...werte) : 0, max: werte.length ? Math.max(...werte) : 1 };

    // Bei Prozentwerten ist die Achse von 0 bis 100 aussagekräftiger als eine,
    // die auf 84–87 zoomt und aus einer ruhigen Platte ein Drama macht.
    let min = reihe.einheit === 'prozent' ? 0 : Math.min(0, roh.min);
    let max = reihe.einheit === 'prozent' ? Math.max(100, roh.max) : roh.max;
    if (grenze != null && grenze > max) max = grenze;
    if (max === min) max = min + 1;

    const spanneSekunden = p.length ? p[p.length - 1].t - p[0].t : 0;
    const innenB = breite - RAND.links - RAND.rechts;
    const innenH = HOEHE - RAND.oben - RAND.unten;

    const x = (i: number) => RAND.links + (p.length > 1 ? (i / (p.length - 1)) * innenB : innenB / 2);
    const y = (v: number) => RAND.oben + innenH - ((v - min) / (max - min)) * innenH;

    // Ein Pfad je zusammenhängendem Stück. Die Trennung an `null` ist der
    // Grund, warum hier nicht einfach ein einziger `d`-String entsteht.
    const stuecke: string[] = [];
    let aktuell: string[] = [];
    p.forEach((punkt, i) => {
      if (punkt.v === null) {
        if (aktuell.length > 1) stuecke.push(aktuell.join(' '));
        aktuell = [];
        return;
      }
      aktuell.push(`${aktuell.length ? 'L' : 'M'}${x(i).toFixed(1)},${y(punkt.v).toFixed(1)}`);
    });
    if (aktuell.length > 1) stuecke.push(aktuell.join(' '));

    return { pfade: stuecke, minY: min, maxY: max, punkte: p, spanne: spanneSekunden };
  }, [reihe, breite, grenze]);

  const innenH = HOEHE - RAND.oben - RAND.unten;
  const yFuer = (v: number) => RAND.oben + innenH - ((v - minY) / (maxY - minY)) * innenH;
  const xFuer = (i: number) =>
    RAND.links + (punkte.length > 1 ? (i / (punkte.length - 1)) * (breite - RAND.links - RAND.rechts) : 0);

  const hatWerte = punkte.some((p) => p.v !== null);
  const gezeigt = zeiger ? punkte[zeiger.i] : null;

  function beiBewegung(e: React.MouseEvent<SVGSVGElement>) {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box || punkte.length < 2) return;
    const relativ = ((e.clientX - box.left) / box.width) * breite;
    const anteil = (relativ - RAND.links) / (breite - RAND.links - RAND.rechts);
    const i = Math.round(Math.min(1, Math.max(0, anteil)) * (punkte.length - 1));
    setZeiger({ x: xFuer(i), i });
  }

  return (
    <div className="w-full">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs font-medium text-slate-600">{reihe.label}</span>
        <span className="font-mono text-xs tabular-nums text-slate-500">
          {gezeigt
            ? gezeigt.v === null
              ? t('metrics.noValue')
              : formatiere(gezeigt.v, reihe.einheit)
            : hatWerte
              ? formatiere(punkte.filter((p) => p.v !== null).slice(-1)[0]!.v!, reihe.einheit)
              : '—'}
        </span>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${breite} ${HOEHE}`}
        className="w-full"
        style={{ height: HOEHE }}
        onMouseMove={beiBewegung}
        onMouseLeave={() => setZeiger(null)}
        role="img"
        aria-label={reihe.label}
      >
        {/* Waagerechte Hilfslinien mit Beschriftung */}
        {[0, 0.5, 1].map((anteil) => {
          const wert = minY + (maxY - minY) * (1 - anteil);
          const y = RAND.oben + innenH * anteil;
          return (
            <g key={anteil}>
              <line
                x1={RAND.links}
                x2={breite - RAND.rechts}
                y1={y}
                y2={y}
                stroke="currentColor"
                className="text-slate-200"
                strokeWidth={1}
              />
              <text
                x={RAND.links - 5}
                y={y + 3}
                textAnchor="end"
                className="fill-slate-400"
                style={{ fontSize: 9, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatiere(wert, reihe.einheit)}
              </text>
            </g>
          );
        })}

        {grenze != null && grenze <= maxY && (
          <>
            <line
              x1={RAND.links}
              x2={breite - RAND.rechts}
              y1={yFuer(grenze)}
              y2={yFuer(grenze)}
              stroke="currentColor"
              className="text-red-400"
              strokeWidth={1}
              strokeDasharray="4 3"
            />
            {grenzeLabel && (
              <text
                x={breite - RAND.rechts}
                y={yFuer(grenze) - 3}
                textAnchor="end"
                className="fill-red-500"
                style={{ fontSize: 9 }}
              >
                {grenzeLabel}
              </text>
            )}
          </>
        )}

        {pfade.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="none"
            stroke="currentColor"
            className="text-brand-600"
            strokeWidth={1.6}
            strokeLinejoin="round"
          />
        ))}

        {zeiger && (
          <line
            x1={zeiger.x}
            x2={zeiger.x}
            y1={RAND.oben}
            y2={HOEHE - RAND.unten}
            stroke="currentColor"
            className="text-slate-400"
            strokeWidth={1}
          />
        )}

        {/* Zeitachse: Anfang, Mitte, Ende. Mehr Beschriftung als das wird auf
            dieser Breite unleserlich. */}
        {punkte.length > 1 &&
          [0, Math.floor(punkte.length / 2), punkte.length - 1].map((i, n) => (
            <text
              key={i}
              x={xFuer(i)}
              y={HOEHE - 5}
              textAnchor={n === 0 ? 'start' : n === 2 ? 'end' : 'middle'}
              className="fill-slate-400"
              style={{ fontSize: 9 }}
            >
              {zeitLabel(punkte[i].t, spanne)}
            </text>
          ))}

        {!hatWerte && (
          <text
            x={breite / 2}
            y={HOEHE / 2}
            textAnchor="middle"
            className="fill-slate-400"
            style={{ fontSize: 11 }}
          >
            {t('metrics.noData')}
          </text>
        )}
      </svg>
    </div>
  );
}
